import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RupiahInput } from "@/components/RupiahInput";
import { useFinance } from "@/context/FinanceContext";
import { QUICK_CATEGORIES } from "@/lib/finance";
import { formatCompact } from "@/lib/currency";

function Section({ title, hint, children, testId }) {
  return (
    <section className="mb-6 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid={testId}>
      <h2 className="mb-1 font-heading text-base font-bold text-zinc-900">{title}</h2>
      {hint && <p className="mb-3 text-xs leading-relaxed text-zinc-500">{hint}</p>}
      {children}
    </section>
  );
}

function ListRow({ name, sub, amount, onDelete, testId }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-100 py-2.5 last:border-0" data-testid={testId}>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-zinc-800">{name}</p>
        {sub && <p className="truncate text-xs text-zinc-400">{sub}</p>}
      </div>
      <div className="flex items-center gap-3 pl-3">
        <span className="font-mono text-sm font-bold text-zinc-900">{formatCompact(amount)}</span>
        <button onClick={onDelete} className="text-zinc-300 hover:text-rose-500" aria-label="Hapus">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function FinanceSetup() {
  const navigate = useNavigate();
  const f = useFinance();
  const d = f.finance || {};

  const [inName, setInName] = useState("");
  const [inAmt, setInAmt] = useState(0);
  const [inOwner, setInOwner] = useState("husband");

  const [exName, setExName] = useState("");
  const [exAmt, setExAmt] = useState(0);

  const [dbName, setDbName] = useState("");
  const [dbPay, setDbPay] = useState(0);

  const [emf, setEmf] = useState(d.emergencyFund?.current_amount || 0);
  const [hh, setHh] = useState(d.household?.name || "");

  const [bgCat, setBgCat] = useState(QUICK_CATEGORIES[0].label);
  const [bgAmt, setBgAmt] = useState(0);

  const addIncome = () => {
    if (!inName || inAmt <= 0) return toast.error("Isi nama & jumlah");
    f.upsertIncome({ name: inName, amount: inAmt, owner: inOwner, type: "salary" });
    setInName(""); setInAmt(0); toast.success("Sumber penghasilan ditambah");
  };
  const addExpense = () => {
    if (!exName || exAmt <= 0) return toast.error("Isi nama & jumlah");
    f.upsertRecurring({ name: exName, amount: exAmt, category: exName, classification: "essential" });
    setExName(""); setExAmt(0); toast.success("Pengeluaran rutin ditambah");
  };
  const addDebt = () => {
    if (!dbName || dbPay <= 0) return toast.error("Isi nama & cicilan/bln");
    f.upsertDebt({ name: dbName, monthly_payment: dbPay });
    setDbName(""); setDbPay(0); toast.success("Cicilan ditambah");
  };
  const addBudget = () => {
    if (bgAmt <= 0) return toast.error("Isi target bulanan");
    f.upsertBudget({ category: bgCat, amount: bgAmt });
    setBgAmt(0); toast.success("Target hemat tersimpan");
  };

  return (
    <div className="px-6 pb-28 pt-8" data-testid="finance-setup-page">
      <header className="mb-5 flex items-center gap-3">
        <button data-testid="setup-back" onClick={() => navigate("/app/profil")} className="text-zinc-500">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="font-heading text-2xl font-extrabold text-zinc-900">Pengaturan Keuangan</h1>
      </header>

      {/* Profil Keluarga */}
      <Section title="Profil Keluarga" testId="setup-household">
        <div className="flex gap-2">
          <Input
            data-testid="hh-name"
            value={hh}
            onChange={(e) => setHh(e.target.value)}
            placeholder="Nama keluarga"
            className="h-11 rounded-2xl border-zinc-200"
          />
          <Button
            data-testid="hh-save"
            onClick={() => { f.setHouseholdName(hh); toast.success("Tersimpan"); }}
            variant="outline"
            className="h-11 rounded-full border-zinc-300 text-sm font-semibold"
          >
            Simpan
          </Button>
        </div>
      </Section>

      {/* Sumber Penghasilan */}
      <Section title="Sumber Penghasilan" hint="Jadi dasar proyeksi & 'anggaran aman/hari' di tab Analisis. Boleh kosong — sistem akan memperkirakannya dari transaksi (hasilnya kurang akurat)." testId="setup-income">
        {(d.incomeSources || []).map((s) => (
          <ListRow
            key={s.id}
            testId={`income-row-${s.id}`}
            name={s.name}
            sub={`${s.owner} · ${s.frequency}`}
            amount={s.amount}
            onDelete={() => f.deleteIncome(s.id)}
          />
        ))}
        <div className="mt-3 space-y-2">
          <Input data-testid="income-name" value={inName} onChange={(e) => setInName(e.target.value)} placeholder="mis. Gaji Suami" className="h-11 rounded-2xl border-zinc-200" />
          <div className="flex gap-2">
            <select data-testid="income-owner" value={inOwner} onChange={(e) => setInOwner(e.target.value)} className="h-11 rounded-2xl border border-zinc-200 bg-white px-3 text-sm">
              <option value="husband">Suami</option>
              <option value="wife">Istri</option>
              <option value="household">Keluarga</option>
              <option value="other">Lainnya</option>
            </select>
            <div className="flex-1"><RupiahInput testId="income-amount" value={inAmt} onChange={setInAmt} /></div>
          </div>
          <Button data-testid="income-add" onClick={addIncome} variant="outline" className="h-10 w-full rounded-full border-zinc-300 text-sm font-semibold">
            <Plus className="mr-1.5 h-4 w-4" /> Tambah penghasilan
          </Button>
        </div>
      </Section>

      {/* Pengeluaran Rutin */}
      <Section title="Pengeluaran Rutin" hint="Dihitung sebagai 'pengeluaran wajib' bulanan — memengaruhi proyeksi akhir bulan dan ukuran ketahanan dana darurat." testId="setup-recurring">
        {(d.recurringExpenses || []).map((s) => (
          <ListRow key={s.id} testId={`recurring-row-${s.id}`} name={s.name} sub={s.frequency} amount={s.amount} onDelete={() => f.deleteRecurring(s.id)} />
        ))}
        <div className="mt-3 space-y-2">
          <Input data-testid="recurring-name" value={exName} onChange={(e) => setExName(e.target.value)} placeholder="mis. KPR / SPP / Internet" className="h-11 rounded-2xl border-zinc-200" />
          <RupiahInput testId="recurring-amount" value={exAmt} onChange={setExAmt} />
          <Button data-testid="recurring-add" onClick={addExpense} variant="outline" className="h-10 w-full rounded-full border-zinc-300 text-sm font-semibold">
            <Plus className="mr-1.5 h-4 w-4" /> Tambah pengeluaran rutin
          </Button>
        </div>
      </Section>

      {/* Cicilan & Utang */}
      <Section title="Cicilan & Utang" hint="Termasuk 'pengeluaran wajib' bulanan Anda, dipakai untuk proyeksi dan hitung ketahanan dana darurat." testId="setup-debt">
        {(d.debts || []).map((s) => (
          <ListRow key={s.id} testId={`debt-row-${s.id}`} name={s.name} sub={`${s.type} · cicilan/bln`} amount={s.monthly_payment} onDelete={() => f.deleteDebt(s.id)} />
        ))}
        <div className="mt-3 space-y-2">
          <Input data-testid="debt-name" value={dbName} onChange={(e) => setDbName(e.target.value)} placeholder="mis. KPR / Mobil / Kartu Kredit" className="h-11 rounded-2xl border-zinc-200" />
          <RupiahInput testId="debt-amount" value={dbPay} onChange={setDbPay} hint="Cicilan per bulan" />
          <Button data-testid="debt-add" onClick={addDebt} variant="outline" className="h-10 w-full rounded-full border-zinc-300 text-sm font-semibold">
            <Plus className="mr-1.5 h-4 w-4" /> Tambah cicilan
          </Button>
        </div>
      </Section>

      {/* Dana Darurat */}
      <Section title="Dana Darurat" hint="Dipakai untuk kartu 'Dana darurat menutup ~X bulan' di tab Analisis & Ringkasan Bulanan. Kalau kosong, tampil 'Belum tersedia'." testId="setup-emergency">
        <RupiahInput testId="emergency-amount" value={emf} onChange={setEmf} hint="Total dana darurat saat ini" />
        <Button
          data-testid="emergency-save"
          onClick={() => { f.setEmergencyFund(emf); toast.success("Dana darurat tersimpan"); }}
          variant="outline"
          className="mt-2 h-10 w-full rounded-full border-zinc-300 text-sm font-semibold"
        >
          Simpan dana darurat
        </Button>
      </Section>

      {/* Target Hemat per kategori */}
      <Section title="Target Hemat (per kategori / bulan)" hint="Batas belanja per kategori. Progres 'tercapai / lewat / sisa' otomatis muncul di tab Analisis & Beranda dari transaksi Anda." testId="setup-budget">
        {(d.budgets || []).map((b) => (
          <ListRow
            key={b.id}
            testId={`budget-setup-row-${b.category}`}
            name={b.category}
            sub="target / bulan"
            amount={b.amount}
            onDelete={() => f.deleteBudget(b.id)}
          />
        ))}
        <div className="mt-3 space-y-2">
          <select
            data-testid="budget-category"
            value={bgCat}
            onChange={(e) => setBgCat(e.target.value)}
            className="h-11 w-full rounded-2xl border border-zinc-200 bg-white px-3 text-sm"
          >
            {QUICK_CATEGORIES.map((c) => (
              <option key={c.id} value={c.label}>{c.label}</option>
            ))}
          </select>
          <RupiahInput testId="budget-amount" value={bgAmt} onChange={setBgAmt} hint="Target maksimal per bulan" />
          <Button data-testid="budget-add" onClick={addBudget} variant="outline" className="h-10 w-full rounded-full border-zinc-300 text-sm font-semibold">
            <Plus className="mr-1.5 h-4 w-4" /> Simpan target kategori
          </Button>
        </div>
        <p className="mt-2 text-xs text-zinc-400">Target dihitung otomatis dari transaksi kategori pada bulan berjalan dan reset tiap bulan.</p>
      </Section>

      <div className="flex items-start gap-2 rounded-2xl bg-emerald-50 p-3">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
        <p className="text-xs leading-relaxed text-emerald-700">
          Angka keuangan yang Anda masukkan tetap pribadi dan tidak dapat dilihat oleh admin CeKKeU.
        </p>
      </div>
    </div>
  );
}
